import React, { FC, PropsWithChildren, ReactNode } from 'react';
import {
    Dialog as MaterialDialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import classNames from 'classnames';
import { ErrorBoundary } from 'components/ErrorBoundary';

import { Text, TextTypography } from '../Text/Text';
import { CloseButton } from '../CloseButton/CloseButton';
import { LargeButton, LargeButtonColor } from '../LargeButton/LargeButton';
import css from './DialogNew.module.css';

export interface DialogProps {
    // Optional:
    title?: string;
    marginTop?: number;
    contentStyles?: string;
    titleStyles?: string;
    fullHeight?: boolean;
    hideCloseButton?: boolean;
    onClose?: () => void;
    onSubmit?: () => void;
    width?: number;
    subTitle?: ReactNode;
    disableContentPadding?: boolean;
    leftButton?: string;
    rightButton?: string;
    isUIEnabled?: boolean;
}

const useStyles = makeStyles(theme => ({
    closeButton: {
        height: theme.spacing(4),
        position: 'absolute',
        right: theme.spacing(3),
        top: theme.spacing(3),
        width: theme.spacing(4),
        [theme.breakpoints.down(321)]: {
            top: theme.spacing(2),
            right: theme.spacing(2),
        },
    },
    content: {
        padding: 0,
        display: 'flex',
        alignItems: 'stretch',
        flexShrink: 0,
        width: '100%',
        marginBottom: 0,
        // [theme.breakpoints.up('sm')]: {
        //     paddingLeft: (props: Partial<DialogProps>) => (props.disableContentPadding ? 0 : theme.spacing(2)),
        //     paddingRight: (props: Partial<DialogProps>) => (props.disableContentPadding ? 0 : theme.spacing(2)),
        // },
    },
    title: {
        padding: theme.spacing(2, 2),
        borderBottom: '1px solid rgba(15, 24, 37, 0.05)',
        [theme.breakpoints.up(480)]: {
            padding: theme.spacing(3, 3),
        },
        [theme.breakpoints.up(640)]: {
            padding: theme.spacing(3, 5),
        },
    },
    footer: {
        borderTop: '1px solid rgba(15, 24, 37, 0.05)',
        padding: theme.spacing(2, 4),
        [theme.breakpoints.down(640)]: {
            padding: theme.spacing(2, 2),
        },
        [theme.breakpoints.down(376)]: {
            padding: theme.spacing(2, 1),
        },
    },
    buttonCancel: {
        padding: theme.spacing(3, 6),
        background: 'rgba(15, 24, 37, 0.1)',
        color: '#243143',
    },
    dividers: {
        padding: theme.spacing(2, 2),
        [theme.breakpoints.up(480)]: {
            padding: theme.spacing(3, 3),
        },
        [theme.breakpoints.up(640)]: {
            padding: theme.spacing(3, 5),
        },
    },
    footerFree: {
        marginTop: 16,
    },
}));

const useDialogStyles = makeStyles(theme => {
    const marginTop = theme.spacing(5);
    const marginTopDesktop = theme.spacing(13);
    const marginBottomDesktop = theme.spacing(12.25);
    return {
        paper: {
            borderTopLeftRadius: theme.spacing(2),
            borderTopRightRadius: theme.spacing(2),
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
            alignSelf: 'flex-end',
            margin: 0,
            marginTop: marginTop,
            width: '100vw',
        },
        paperScrollPaper: {
            maxHeight: `calc(100vh - ${marginTop}px)`,
            height: (props: DialogProps) => (props.fullHeight ? '100%' : 'auto'),
        },
        [theme.breakpoints.up('sm')]: {
            paper: {
                alignSelf: 'flex-start',
                borderRadius: 20,
                marginTop: (props: DialogProps) => props.marginTop ?? marginTopDesktop,
                marginBottom: marginBottomDesktop,
                width: (props: DialogProps) => props.width ?? 573,
                maxWidth: '90vw',
            },
            paperScrollPaper: {
                maxHeight: `calc(100vh - ${marginTopDesktop + marginBottomDesktop}px)`,
            },
        },
        [theme.breakpoints.between(479, 639)]: {
            paper: {
                maxWidth: '90vw',
            },
        },
    };
});

export const DialogNew: FC<DialogProps> = (props: PropsWithChildren<DialogProps>) => {
    const classes = useStyles(props);
    const dialogClasses = useDialogStyles(props);
    const { subTitle } = props;

    return (
        <ErrorBoundary>
            <MaterialDialog open={true} onClose={props.onClose} classes={dialogClasses}>
                {props.title && (
                    <DialogTitle
                        className={classNames(classes.title, props.titleStyles)}
                        disableTypography={true}
                        id="scroll-dialog-title"
                    >
                        <Text typography={TextTypography.DISPLAY_STRONG_CHANGE}>{props.title}</Text>
                        {!props.hideCloseButton && (
                            <div className={classes.closeButton}>
                                <CloseButton onClick={props.onClose} />
                            </div>
                        )}
                        {subTitle}
                    </DialogTitle>
                )}
                <DialogContent className={classes.dividers}>
                    <DialogContentText
                        className={classNames(classes.content, props.contentStyles)}
                        id="scroll-dialog-description"
                    >
                        {props.children}
                    </DialogContentText>
                </DialogContent>
                {props.leftButton && props.rightButton ? (
                    <DialogActions>
                        <Grid container spacing={2} className={classes.footer}>
                            <Grid item xs={6}>
                                <LargeButton onClick={props.onClose} color={LargeButtonColor.GRAY}>
                                    <div>
                                        <Text typography={TextTypography.PARA_ACCENT}>{props.leftButton}</Text>
                                    </div>
                                </LargeButton>
                            </Grid>
                            <Grid item xs={6}>
                                <LargeButton disabled={!props.isUIEnabled} type="submit" onClick={props.onSubmit}>
                                    <div>
                                        <Text typography={TextTypography.PARA_ACCENT} className={css.text_color_white}>
                                            {props.rightButton}
                                        </Text>
                                    </div>
                                </LargeButton>
                            </Grid>
                        </Grid>
                    </DialogActions>
                ) : (
                    <div className={classes.footerFree}></div>
                )}
            </MaterialDialog>
        </ErrorBoundary>
    );
};
